import { useQuery } from '@apollo/client';
import { Box, MenuItem, type SelectChangeEvent } from '@mui/material';
import type { useFormik } from 'formik';
import {
  useCallback,
  useMemo,
  type ComponentProps,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSelect, DetailSwitch, DetailTextField } from '~/components/forms/details';
import {
  SettingDescription,
  SettingFieldRequired,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { useAppContext } from '~/contexts';
import { DeviceSignalKind } from '~/generated/graphql';
import {
  ActionAutocomplete,
  ChannelAutocomplete,
  EventAutocomplete,
  EventOffset,
  EventTypeAutocomplete,
  OverlayAutocomplete,
  ShowAutocomplete,
  TargetAutocomplete,
  ViewAutocomplete,
} from '.';
import type { SignalInput, SignalPatch } from '../lib/validation';
import { SignalEventTypesDocument } from './EventTypeAutocomplete/EventTypeAutocomplete.generated';

export interface DeviceSignalFormProps<T extends SignalInput | SignalPatch>
  extends Omit<ComponentProps<'form'>, 'ref' | 'onSubmit'> {
  formik: ReturnType<typeof useFormik<T>>;
}

export const DeviceSignalForm = <T extends SignalInput | SignalPatch>({
  formik,
  ...formProps
}: DeviceSignalFormProps<T>) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(SignalEventTypesDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const eventTypes = useMemo(() => data?.network?.allEventTypes ?? [], [data]);

  const fieldProps = useCallback(
    (name: string) => {
      const meta = formik.getFieldMeta(name);

      return {
        error: meta.touched && !!meta.error,
        helperText: (meta.touched && meta.error) || ' ',
        name,
        onBlur: formik.handleBlur,
        value: meta.value ?? null,
      };
    },
    [formik],
  );

  const newRecord = 'networkId' in formik.values;

  const handleEventNameChange = useCallback(
    async (_: SyntheticEvent, name: string | null) => {
      const value =
        name == null
          ? {}
          : [
              'CHL_END_GAME',
              'COMMON_START_TIME',
              'MILB_END_GAME',
              'MLB_END_GAME',
              'MLS_END_GAME',
              'NBA_END_GAME',
              'NCAAF_END_TIME',
              'NFL_END_TIME',
              'NHL_END_GAME',
              'NWSL_END_GAME',
            ].includes(name)
          ? { name, offset: 0 }
          : { name };

      await formik.setFieldValue('event', value);
    },
    [formik],
  );

  const handleEventTypeChange = useCallback(
    async (_: SyntheticEvent, value: number | null) => {
      await Promise.all([
        formik.setFieldValue('event', {}),
        formik.setFieldValue('eventTypeId', value),
      ]);
    },
    [formik],
  );

  const handleKindChange = useCallback(
    async (event: SelectChangeEvent<unknown>, _: ReactNode) => {
      await formik.setFieldValue('eventTypeId', null);
      await formik.setFieldValue('event', {});
      formik.handleChange(event);
    },
    [formik],
  );

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      {...formProps}
    >
      <Box>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.name')}
                <SettingFieldRequired>
                  {t('pages.device_signals.settings.fields.name.required')}
                </SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.device_signals.settings.fields.name.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                {...fieldProps('name')}
                autoFocus
                disabled={formik.isSubmitting}
                fullWidth
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>{t('common.status.enabled')}</SettingName>
              <SettingDescription>
                {t('pages.device_signals.settings.fields.enabled.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSwitch
                checked={formik.values.enabled ?? undefined}
                color="primary"
                disabled={formik.isSubmitting}
                name="enabled"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </Box>

      <Box>
        <SettingsHead>{t('common.labels.type')}</SettingsHead>
        <SettingsBody>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.type')}
                <SettingFieldRequired>
                  {t('pages.device_signals.settings.fields.kind.required')}
                </SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.device_signals.settings.fields.kind.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSelect
                {...fieldProps('kind')}
                disabled={!newRecord || formik.isSubmitting}
                onChange={handleKindChange}
              >
                {Object.values(DeviceSignalKind).map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {t(`pages.device_signals.settings.fields.kind.${kind.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </DetailSelect>

              {formik.values.kind === DeviceSignalKind.Event && (
                <Box sx={{ marginTop: 2 }}>
                  <EventTypeAutocomplete
                    {...fieldProps('eventTypeId')}
                    disabled={formik.isSubmitting}
                    eventTypes={eventTypes}
                    onChange={handleEventTypeChange}
                  />

                  <EventAutocomplete
                    {...fieldProps('event.name')}
                    disabled={formik.isSubmitting}
                    onChange={handleEventNameChange}
                    signalType={
                      eventTypes.find((x) => x.id === formik.values.eventTypeId)?.signalType ??
                      'COMMON'
                    }
                  />
                </Box>
              )}

              {'offset' in (formik.values.event ?? {}) && (
                <EventOffset
                  {...fieldProps('event.offset')}
                  eventName={formik.getFieldMeta('event.name').value ?? null}
                  onChange={(value) => {
                    void formik.setFieldTouched('event.offset');
                    void formik.setFieldValue('event.offset', value);
                  }}
                />
              )}
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </Box>

      <Box>
        <SettingsHead>{t('pages.device_signals.settings.sections.target')}</SettingsHead>
        <SettingsBody>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.device_signals.settings.fields.target.label')}
                <SettingFieldRequired>
                  {t('pages.device_signals.settings.fields.target.required')}
                </SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.device_signals.settings.fields.target.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <TargetAutocomplete
                {...fieldProps('target')}
                disabled={formik.isSubmitting}
                onChange={(value) => formik.setFieldValue('target', value)}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </Box>

      <Box>
        <SettingsHead>{t('common.labels.action')}</SettingsHead>
        <SettingsBody>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.action')}
                <SettingFieldRequired>
                  {t('pages.device_signals.settings.fields.action_command.required')}
                </SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.device_signals.settings.fields.action_command.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <ActionAutocomplete
                name="action.command"
                disabled={formik.isSubmitting}
                error={formik.touched.action && typeof formik.errors.action === 'string'}
                helperText={
                  formik.touched.action && typeof formik.errors.action === 'string'
                    ? formik.errors.action
                    : ' '
                }
                onBlur={formik.handleBlur}
                onChange={(_event, value) => {
                  void formik.setFieldValue('action', value);
                }}
                value={formik.values.action ?? null}
              />

              <Box>
                {formik.values.action?.command === 'play_overlay' && (
                  <OverlayAutocomplete
                    {...fieldProps('action.id')}
                    disabled={formik.isSubmitting}
                    onChange={(_event, value) => formik.setFieldValue('action.id', value)}
                  />
                )}

                {formik.values.action?.command === 'play_show' && (
                  <ShowAutocomplete
                    {...fieldProps('action.id')}
                    disabled={formik.isSubmitting}
                    onChange={(_event, value) => formik.setFieldValue('action.id', value)}
                  />
                )}

                {formik.values.action?.command === 'trigger_view' && (
                  <ViewAutocomplete
                    {...fieldProps('action.id')}
                    disabled={formik.isSubmitting}
                    onChange={(_event, value) => formik.setFieldValue('action.id', value)}
                  />
                )}

                {formik.values.action?.command === 'tune_channel' && (
                  <ChannelAutocomplete
                    {...fieldProps('action.id')}
                    disabled={formik.isSubmitting}
                    onChange={(_event, value) => formik.setFieldValue('action.id', value)}
                  />
                )}
              </Box>
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </Box>
    </Box>
  );
};
